import { Box, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { StoryPageLayout } from '../layouts';

/**
 * GenAI Governance & Guardrails Assessment
 *
 * This page showcases the GenAI governance assessment offering.
 * The STORY is the primary feature, demonstrating value through assessment details.
 * The form sidebar provides easy access for interested prospects to request assessment.
 *
 * STRUCTURE:
 * - Dark header with page title
 * - Hero image (GenAI governance visual)
 * - Two-column layout (story content 2/3 left, form 1/3 right sticky)
 * - Statistics section
 * - Assessment overview with pricing
 * - "Rapid GenAI Discovery" section (2 weeks)
 * - "Governance Framework in 30 Days" section
 * - Form sidebar with sticky positioning (desktop) or stacked below (mobile)
 *
 * SEO OPTIMIZATIONS:
 * - JSON-LD structured data (Service schema) for AI crawlers
 * - Semantic HTML5 elements (article, section)
 * - Optimized heading hierarchy (H1 → H2 → H3)
 * - Meta tags and Open Graph for social sharing
 *
 * @returns {import('react').ReactElement} GenAI Assessment page
 */
function GenAIAssessment() {
  // Structured data for AI crawlers and search engines
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'GenAI Governance & Guardrails Assessment',
    alternateName: 'No Is Not a Policy',
    description:
      'RSM Collective and SurePath AI deliver a 30-day Proof of Concept (POC) to Production assessment that transforms unmanaged GenAI usage into a secure, compliant, and governed platform.',
    provider: {
      '@type': 'Organization',
      name: 'RSM Collective',
      partner: {
        '@type': 'Organization',
        name: 'SurePath AI',
      },
    },
    offers: {
      '@type': 'Offer',
      price: '10000.00',
      priceCurrency: 'USD',
      priceValidUntil: '2026-12-31',
      availability: 'https://schema.org/InStock',
      itemOffered: {
        '@type': 'Service',
        name: '30-Day POC to Production Assessment',
        description:
          'Transform unmanaged GenAI usage into a secure, compliant, and governed platform',
      },
    },
    serviceType: 'GenAI Governance Assessment',
    areaServed: {
      '@type': 'Country',
      name: 'United States',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Assessment Phases',
      itemListElement: [
        {
          '@type': 'Service',
          name: 'Rapid GenAI Discovery',
          duration: 'P2W',
          description:
            'Identify all GenAI tools and data flows in use (including Shadow AI). Quantify ROI and usage trends across departments.',
        },
        {
          '@type': 'Service',
          name: 'Governance Framework in 30 Days',
          duration: 'P30D',
          description:
            'Define policies and enforce guardrails for compliant GenAI use. Implement role-based access controls, content, data redaction, and classification.',
        },
        {
          '@type': 'Service',
          name: 'Transition to Production',
          description:
            'Deploy the SurePath AI Governance Platform for ongoing monitoring, automate compliance reporting, and enable safe access to public and private GenAI models.',
        },
      ],
    },
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>
          GenAI Governance & Guardrails Assessment - No Is Not a Policy | RSM
          Collective
        </title>
        <meta
          name="description"
          content="Transform unmanaged GenAI usage into a secure, compliant platform. 30-day POC to Production assessment from RSM Collective & SurePath AI. $10,000 fixed price."
        />

        {/* Open Graph tags for social sharing */}
        <meta
          property="og:title"
          content="GenAI Governance & Guardrails Assessment"
        />
        <meta
          property="og:description"
          content="30-day assessment to transform GenAI usage into secure, compliant governance. Fixed price $10,000."
        />
        <meta property="og:type" content="product" />
        <meta
          property="og:url"
          content="https://www.rsmc.ai/assessment/genai-governance"
        />
        <meta
          property="og:image"
          content="https://www.rsmc.ai/images/genai-governance-guardrails-assessment.png"
        />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="GenAI Governance & Guardrails Assessment"
        />
        <meta
          name="twitter:description"
          content="30-day POC to Production assessment - $10,000 fixed price"
        />

        {/* Additional SEO signals */}
        <meta
          name="keywords"
          content="GenAI governance, AI guardrails, generative AI compliance, shadow AI, AI security assessment, AWS Bedrock, AI governance platform"
        />
        <link
          rel="canonical"
          href="https://www.rsmc.ai/assessment/genai-governance"
        />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <StoryPageLayout
        title="No Is Not a Policy"
        subtitle="GenAI Governance & Guardrails Assessment"
        formHeading="Get Your GenAI Governance Assessment"
        formFilePath="/forms/genai-assessment-form.txt"
        heroImage="/images/genai-governance-guardrails-assessment.png"
        heroImageAlt="GenAI governance and guardrails assessment framework - securing enterprise AI with compliance and policy controls"
        errorMessage="Unable to load assessment form"
        loadingMessage="Loading assessment form..."
      >
        {/* Introduction Section with Statistics */}
        <Box
          sx={{
            mb: { xs: 6, md: 8 },
            borderLeft: {
              xs: '4px solid var(--color-primary)',
              md: '6px solid var(--color-primary)',
            },
            pl: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              fontWeight: 400,
            }}
          >
            Generative AI adoption is accelerating, and Shadow AI is already
            part of most organizations whether approved or not. With 75% of
            employees using GenAI tools and 68% of enterprises worried about
            data exposure, CISOs cannot manage GenAI by simply saying no. Real
            governance begins with visibility, accountability, and control.
          </Typography>
        </Box>

        {/* Assessment Heading */}
        <Box
          sx={{
            mb: { xs: 4, md: 5 },
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: 'var(--color-heading)',
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
            }}
          >
            GenAI Governance & Guardrails Assessment
          </Typography>
        </Box>

        {/* Assessment Description */}
        <Box
          sx={{
            mb: { xs: 6, md: 8 },
            borderLeft: {
              xs: '4px solid var(--color-primary)',
              md: '6px solid var(--color-primary)',
            },
            pl: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.0625rem',
              lineHeight: 1.75,
              color: 'var(--color-text)',
              fontWeight: 400,
            }}
          >
            RSM Collective and SurePath AI deliver a 30-day Proof of Concept
            (POC) to Production assessment that transforms unmanaged GenAI usage
            into a secure, compliant, and governed platform. The result is the
            safe and effective adoption of GenAI tools and services across your
            workforce.
          </Typography>
        </Box>

        {/* Visual separator - R logo */}
        <Box
          sx={{
            textAlign: 'center',
            my: 6,
          }}
        >
          <Box
            component="img"
            src="/images/r-logo.png"
            alt="Section separator"
            sx={{
              width: '40px',
              height: 'auto',
              opacity: 0.7,
            }}
          />
        </Box>

        {/* Rapid GenAI Discovery Section */}
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            border: '3px solid var(--color-border)',
            borderRadius: '0 12px 0 12px',
            backgroundColor: 'var(--color-card)',
            mb: { xs: 6, md: 10 },
            '&:hover': {
              borderColor: 'var(--color-primary)',
              transform: 'translateY(-4px)',
              transition: 'all 0.3s ease',
              boxShadow:
                '0 8px 24px color-mix(in srgb, var(--color-primary) 15%, transparent)',
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'var(--color-primary)',
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              textAlign: 'center',
            }}
          >
            Rapid GenAI Discovery
          </Typography>
          <Typography
            sx={{
              fontWeight: 600,
              mb: 3,
              color: 'var(--color-heading)',
              fontSize: { xs: '0.95rem', md: '1rem' },
              textAlign: 'center',
            }}
          >
            2 Weeks
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 0,
              listStyle: 'none',
              '& li': {
                fontSize: '0.95rem',
                lineHeight: 1.6,
                color: 'var(--color-text)',
                mb: 2,
                pl: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '0.6em',
                  width: '6px',
                  height: '6px',
                  backgroundColor: 'var(--color-primary)',
                  borderRadius: '50%',
                },
              },
            }}
          >
            <li>
              Identify all GenAI tools and data flows in use (including Shadow
              AI). Quantify ROI and usage trends across departments.
            </li>
            <li>Assess exposure risks and compliance gaps.</li>
            <li>
              Integrate with existing, AWS, Palo Alto, Netskope, & Zscaler
              environments.
            </li>
          </Box>
        </Box>

        {/* Visual separator - R logo */}
        <Box
          sx={{
            textAlign: 'center',
            my: 6,
          }}
        >
          <Box
            component="img"
            src="/images/r-logo.png"
            alt="Section separator"
            sx={{
              width: '40px',
              height: 'auto',
              opacity: 0.7,
            }}
          />
        </Box>

        {/* Governance Framework Section */}
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            border: '3px solid var(--color-border)',
            borderRadius: '0 12px 0 12px',
            backgroundColor: 'var(--color-card)',
            mb: { xs: 6, md: 10 },
            '&:hover': {
              borderColor: 'var(--color-primary)',
              transform: 'translateY(-4px)',
              transition: 'all 0.3s ease',
              boxShadow:
                '0 8px 24px color-mix(in srgb, var(--color-primary) 15%, transparent)',
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'var(--color-primary)',
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              textAlign: 'center',
            }}
          >
            Governance Framework in 30 Days
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 0,
              listStyle: 'none',
              '& li': {
                fontSize: '0.95rem',
                lineHeight: 1.6,
                color: 'var(--color-text)',
                mb: 2,
                pl: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '0.6em',
                  width: '6px',
                  height: '6px',
                  backgroundColor: 'var(--color-primary)',
                  borderRadius: '50%',
                },
              },
            }}
          >
            <li>
              Define policies and enforce guardrails for compliant GenAI use.
            </li>
            <li>
              Implement role-based access controls, content, data redaction, and
              classification.
            </li>
            <li>
              Establish secure connections to AWS IAM, Amazon Bedrock, Amazon
              S3, AWS IAM, CloudTrail & CloudWatch.
            </li>
          </Box>
        </Box>

        {/* Visual separator - R logo */}
        <Box
          sx={{
            textAlign: 'center',
            my: 6,
          }}
        >
          <Box
            component="img"
            src="/images/r-logo.png"
            alt="Section separator"
            sx={{
              width: '40px',
              height: 'auto',
              opacity: 0.7,
            }}
          />
        </Box>

        {/* Transition to Production Section */}
        <Box
          sx={{
            p: { xs: 3, md: 4 },
            border: '3px solid var(--color-border)',
            borderRadius: '0 12px 0 12px',
            backgroundColor: 'var(--color-card)',
            mb: { xs: 6, md: 10 },
            '&:hover': {
              borderColor: 'var(--color-primary)',
              transform: 'translateY(-4px)',
              transition: 'all 0.3s ease',
              boxShadow:
                '0 8px 24px color-mix(in srgb, var(--color-primary) 15%, transparent)',
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              mb: 2,
              color: 'var(--color-primary)',
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              textAlign: 'center',
            }}
          >
            Transition to Production
          </Typography>
          <Box
            component="ul"
            sx={{
              pl: 0,
              listStyle: 'none',
              '& li': {
                fontSize: '0.95rem',
                lineHeight: 1.6,
                color: 'var(--color-text)',
                mb: 2,
                pl: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '0.6em',
                  width: '6px',
                  height: '6px',
                  backgroundColor: 'var(--color-primary)',
                  borderRadius: '50%',
                },
              },
            }}
          >
            <li>
              Deploy the SurePath AI Governance Platform for ongoing monitoring,
              automate compliance reporting, and enable safe access to public
              and private GenAI models.
            </li>
            <li>Full visibility into all GenAI usage within 10 days.</li>
            <li>Operationalized governance in 30 days.</li>
          </Box>
        </Box>

        {/* Pricing Section */}
        <Box
          sx={{
            mb: { xs: 4, md: 6 },
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h3"
            component="div"
            sx={{
              fontWeight: 800,
              mb: 2,
              color: 'var(--color-heading)',
              fontSize: { xs: '1.5rem', md: '1.75rem' },
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
            }}
          >
            <span>GenAI Governance & Guardrails Assessment: Price </span>
            <Box component="data" value="10000" sx={{ fontWeight: 800 }}>
              $10,000.00
            </Box>
          </Typography>
        </Box>
      </StoryPageLayout>
    </>
  );
}

export default GenAIAssessment;
